/* organization/OrgProfile — User profile page within the Org context. */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead } from '../platform/_ui';
import * as authApi from '../api/auth.api.js';

const getSessionUser = () => {
  if (window.__nx_auth?.user) return window.__nx_auth.user;
  try { return JSON.parse(localStorage.getItem('nx-session-v1')); } catch (_) { return null; }
};

const Field = ({ label, value, mono }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text)', fontFamily: mono ? 'monospace' : undefined }}>{value || '—'}</span>
  </div>
);

export default function OrgProfile({ data }) {
  const { orgId }    = useParams();
  const toast        = useToast();
  const [user, setUser]         = useState(getSessionUser);
  const [editForm, setEditForm] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const [pwForm, setPwForm]     = useState({ current: '', next: '', confirm: '' });
  const [pwBusy, setPwBusy]     = useState(false);
  const [pwErr,  setPwErr]      = useState('');
  const [showPw, setShowPw]     = useState(false);

  const org = data.state.organizations.find(o => o.id === orgId);
  const members = (data.state.members || []).filter(m => m.org === orgId);
  const myMember = user?.email ? members.find(m => m.email === user.email) : null;

  useEffect(() => {
    const h = (e) => { if (e.detail?.user) setUser(e.detail.user); };
    window.addEventListener('nx-session-changed', h);
    return () => window.removeEventListener('nx-session-changed', h);
  }, []);

  if (!org) return null;

  const name     = user?.name  || myMember?.name  || 'Org Admin';
  const email    = user?.email || myMember?.email || '';
  const orgRole  = myMember?.orgRole || 'Org Admin';
  const initials = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  const handleSave = async () => {
    setEditBusy(true);
    try {
      await authApi.updateProfile({ name: editForm.name, phone: editForm.phone });
      setUser(u => ({ ...u, name: editForm.name, phone: editForm.phone }));
      setEditForm(null);
      toast({ tone: 'success', icon: 'Check', title: 'Profile updated' });
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Update failed', desc: err?.message });
    } finally { setEditBusy(false); }
  };

  const handlePasswordChange = async () => {
    setPwErr('');
    if (!pwForm.current) { setPwErr('Current password is required.'); return; }
    if (pwForm.next.length < 8) { setPwErr('New password must be at least 8 characters.'); return; }
    if (pwForm.next !== pwForm.confirm) { setPwErr('Passwords do not match.'); return; }
    setPwBusy(true);
    try {
      await authApi.changePassword(pwForm.current, pwForm.next);
      toast({ tone: 'success', icon: 'Key', title: 'Password changed', desc: 'Your password has been updated.' });
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      setPwErr(err?.message || 'Password change failed. Check your current password.');
    } finally { setPwBusy(false); }
  };

  return (
    <div className="nx-cc-page">
      <PageHead
        title="My Profile"
        sub={'Account details and security settings for ' + org.name}
        actions={
          editForm ? null :
          <Btn variant="secondary" icon="Pencil" onClick={() => setEditForm({ name, phone: user?.phone || '' })}>
            Edit Profile
          </Btn>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 20, maxWidth: 900 }}>
        {/* Avatar + Identity */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="nx-cc-chart-card" style={{ padding: 24, textAlign: 'center' }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%',
              background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 26, fontWeight: 700, margin: '0 auto 12px',
            }}>
              {initials}
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--nx-text)' }}>{name}</div>
            <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', margin: '4px 0 12px' }}>{email}</div>
            <Pill tone="blue">{orgRole}</Pill>
          </div>

          <div className="nx-cc-chart-card" style={{ padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Organization</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Field label="Organization" value={org.name} />
              <Field label="Plan" value={org.plan} />
              <Field label="Status" value={org.status ? org.status.charAt(0).toUpperCase() + org.status.slice(1).toLowerCase() : '—'} />
            </div>
          </div>
        </div>

        {/* Profile Details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="nx-cc-chart-card" style={{ padding: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>Profile Information</div>
            {editForm ? (
              <div className="nx-form" style={{ gap: 12 }}>
                <div className="nx-form-row">
                  <label>Full Name</label>
                  <input className="nx-input" autoFocus value={editForm.name}
                    onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && handleSave()} />
                </div>
                <div className="nx-form-row">
                  <label>Phone</label>
                  <input className="nx-input" type="tel" value={editForm.phone}
                    onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && handleSave()}
                    placeholder="+1 (555) 000-0000" />
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <Btn variant="ghost" icon="X" onClick={() => setEditForm(null)}>Cancel</Btn>
                  <Btn variant="primary" icon="Check" disabled={editBusy || !editForm.name.trim()} onClick={handleSave}>
                    {editBusy ? 'Saving…' : 'Save Changes'}
                  </Btn>
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <Field label="Full Name" value={name} />
                <Field label="Email Address" value={email} />
                <Field label="Phone" value={user?.phone || '—'} />
                <Field label="Role" value={orgRole} />
              </div>
            )}
          </div>

          <div className="nx-cc-chart-card" style={{ padding: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>Change Password</div>
            <div className="nx-form" style={{ gap: 12, maxWidth: 400 }}>
              <div className="nx-form-row">
                <label>Current Password</label>
                <input className="nx-input" type={showPw ? 'text' : 'password'}
                  value={pwForm.current}
                  onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && handlePasswordChange()} />
              </div>
              <div className="nx-form-row">
                <label>New Password</label>
                <input className="nx-input" type={showPw ? 'text' : 'password'}
                  value={pwForm.next}
                  onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && handlePasswordChange()}
                  placeholder="Minimum 8 characters" />
              </div>
              <div className="nx-form-row">
                <label>Confirm New Password</label>
                <input className="nx-input" type={showPw ? 'text' : 'password'}
                  value={pwForm.confirm}
                  onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && handlePasswordChange()} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: 'var(--nx-text-muted)' }}>
                <input type="checkbox" checked={showPw} onChange={e => setShowPw(e.target.checked)} />
                Show passwords
              </label>
              {pwErr && (
                <div style={{ fontSize: 12, color: 'var(--nx-danger)', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <NexusIcon name="AlertCircle" size={12} />{pwErr}
                </div>
              )}
              <Btn variant="primary" icon="Key"
                disabled={pwBusy || !pwForm.current || !pwForm.next || !pwForm.confirm}
                onClick={handlePasswordChange}>
                {pwBusy ? 'Changing…' : 'Change Password'}
              </Btn>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
